import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Bilingual A4 catalog: prints all categories, services and offers on a single
 * portrait page (auto-fits with smaller fonts when there are many entries).
 *
 * Receives: { categories, services, offers }
 *   - categories: [{ id, name_ar, name_en, display_order, is_visible }]
 *   - services:   [{ id, category_id, name_ar, name_en, price, duration, is_visible }]
 *   - offers:     [{ id, title_ar, title_en, original_price, discount_price, end_date, is_visible }]
 */
const ServicesCatalogSheet = React.forwardRef(({ categories = [], services = [], offers = [], showContactQRs = false }, ref) => {
  const bookingUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/public-booking`
      : 'https://kegentssaloon.com/public-booking';

  // Public contact channels used for QR codes (loyalty + booking handled separately)
  const contactQRs = [
    {
      label_ar: 'الواتساب',
      label_en: 'WhatsApp',
      sublabel: '+971 56 981 0665',
      value: 'https://wa.me/971569810665',
      color: '#25D366',
    },
    {
      label_ar: 'انستقرام',
      label_en: 'Instagram',
      sublabel: '@kegentssaloon',
      value: 'https://www.instagram.com/kegentssaloon/',
      color: '#E1306C',
    },
    {
      label_ar: 'تيك توك',
      label_en: 'TikTok',
      sublabel: '@ke.gents.saloon.u',
      value: 'https://www.tiktok.com/@ke.gents.saloon.u',
      color: '#111111',
    },
    {
      label_ar: 'موقع الصالون',
      label_en: 'Visit Salon',
      sublabel: 'Al Haboob 2, UAQ',
      value: 'https://maps.google.com/?q=KE+Gents+Saloon+Al+Haboob+2+Umm+Al+Quwain',
      color: '#1d4ed8',
    },
  ];

  // Filter visible only & sort
  const visibleCats = [...categories]
    .filter((c) => c.is_visible !== false)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const visibleSvcs = services.filter((s) => s.is_visible !== false);
  const visibleOffers = offers.filter((o) => o.is_visible !== false);

  // Auto-fit: if there's a lot to print, shrink fonts
  const totalEntries = visibleSvcs.length + visibleOffers.length + visibleCats.length;
  const dense = totalEntries > 35;

  const baseTitleSize = dense ? 13 : 15;
  const baseSvcSize = dense ? 10 : 11.5;
  const baseRowGap = dense ? 2 : 4;
  const sectionGap = dense ? 8 : 14;

  const sectionStyle = {
    marginBottom: sectionGap,
    pageBreakInside: 'avoid',
    breakInside: 'avoid',
  };
  const catTitle = {
    fontSize: baseTitleSize,
    fontWeight: 800,
    margin: '0 0 6px 0',
    color: '#111',
    borderBottom: '2px solid #d4af37',
    paddingBottom: 3,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  const svcRow = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontSize: baseSvcSize,
    lineHeight: 1.4,
    padding: `${baseRowGap}px 0`,
    borderBottom: '1px dotted #eee',
  };

  const fmtPrice = (n) => `${Number(n || 0).toFixed(0)} د.إ`;

  const offersByService = {};
  visibleOffers.forEach((o) => {
    (o.service_ids || []).forEach((sid) => {
      offersByService[sid] = o;
    });
  });

  return (
    <div
      ref={ref}
      style={{
        padding: '10mm 12mm',
        fontFamily: 'Cairo, Tajawal, Arial, sans-serif',
        background: '#fff',
        color: '#111',
        width: '210mm',
        height: '295mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        dir="ltr"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          gap: 14,
          marginBottom: 12,
          borderBottom: '3px double #d4af37',
          paddingBottom: 10,
        }}
      >
        <div style={{ textAlign: 'left' }} dir="ltr">
          <div style={{ fontSize: 10, color: '#666' }}>Scan to book online</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: 10, color: '#666' }}>Services &amp; Offers Catalog</div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
          }}
        >
          <img
            src="/logo-gold.png"
            alt="KE Gents Saloon"
            style={{ height: 80, width: 'auto', display: 'block' }}
            crossOrigin="anonymous"
          />
          <div
            style={{
              padding: 5,
              background: '#fff',
              border: '2px solid #d4af37',
              borderRadius: 6,
              display: 'inline-block',
              lineHeight: 0,
            }}
          >
            <QRCodeSVG
              value={bookingUrl}
              size={80}
              bgColor="#ffffff"
              fgColor="#111111"
              level="M"
              marginSize={0}
            />
          </div>
        </div>

        <div style={{ textAlign: 'right' }} dir="rtl">
          <div style={{ fontSize: 10, color: '#666' }}>امسح للحجز عبر الإنترنت</div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>KE Gents Saloon</div>
          <div style={{ fontSize: 10, color: '#666' }}>قائمة الخدمات والعروض</div>
        </div>
      </div>

      {/* Special Offers banner (always at top if any) */}
      {visibleOffers.length > 0 && (
        <div
          style={{
            ...sectionStyle,
            background: 'linear-gradient(90deg, #fef3c7, #fde68a)',
            border: '1.5px solid #d4af37',
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: sectionGap,
          }}
        >
          <h2
            style={{
              fontSize: baseTitleSize + 1,
              margin: '0 0 6px 0',
              color: '#92400e',
              fontWeight: 800,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>🏷️ العروض الخاصة</span>
            <span style={{ direction: 'ltr' }}>Special Offers 🏷️</span>
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: visibleOffers.length > 3 ? '1fr 1fr' : '1fr',
              gap: 6,
            }}
          >
            {visibleOffers.map((o) => {
              const discount = o.original_price && o.discount_price
                ? Math.round(((o.original_price - o.discount_price) / o.original_price) * 100)
                : 0;
              return (
                <div
                  key={o.id}
                  style={{
                    fontSize: baseSvcSize,
                    background: '#ffffff80',
                    padding: '4px 8px',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {o.title_ar} / {o.title_en}
                  </span>
                  <span style={{ fontWeight: 700, color: '#92400e', whiteSpace: 'nowrap' }}>
                    <span style={{ textDecoration: 'line-through', color: '#999', marginInlineEnd: 4 }}>
                      {fmtPrice(o.original_price)}
                    </span>
                    {fmtPrice(o.discount_price)}
                    {discount > 0 && (
                      <span style={{ marginInlineStart: 4, color: '#dc2626' }}>(-{discount}%)</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories grid (2 columns to fit more) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: visibleCats.length > 3 ? '1fr 1fr' : '1fr',
          gap: 14,
          flex: '1 1 auto',
        }}
      >
        {visibleCats.map((cat) => {
          const catSvcs = visibleSvcs
            .filter((s) => s.category_id === cat.id)
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          if (catSvcs.length === 0) return null;
          return (
            <div key={cat.id} style={sectionStyle}>
              <h3 style={catTitle}>
                <span>{cat.name_ar}</span>
                <span style={{ direction: 'ltr', color: '#666', fontWeight: 600, fontSize: baseTitleSize - 2 }}>
                  {cat.name_en}
                </span>
              </h3>
              <div>
                {catSvcs.map((s) => {
                  const offer = offersByService[s.id];
                  return (
                    <div key={s.id} style={svcRow}>
                      <div style={{ flex: 1, paddingInlineEnd: 8 }}>
                        <span style={{ fontWeight: 600 }}>{s.name_ar}</span>
                        <span style={{ color: '#666', marginInlineStart: 6 }}>/ {s.name_en}</span>
                        {s.duration && (
                          <span style={{ color: '#999', marginInlineStart: 6, fontSize: baseSvcSize - 1 }}>
                            ({s.duration} د / min)
                          </span>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {offer ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#999', marginInlineEnd: 4 }}>
                              {fmtPrice(s.price)}
                            </span>
                            <span style={{ color: '#dc2626' }}>{fmtPrice(offer.discount_price)}</span>
                          </>
                        ) : (
                          fmtPrice(s.price)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contact QR codes strip (only when explicitly enabled, e.g. public catalog) */}
      {showContactQRs && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 6,
            borderTop: '2px dashed #d4af37',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 4,
            }}
          >
            <span style={{ fontSize: baseSvcSize - 0.5, fontWeight: 700, color: '#111' }}>
              📱 امسح أحد الأكواد للتواصل
            </span>
            <span style={{ fontSize: baseSvcSize - 0.5, fontWeight: 700, color: '#111', direction: 'ltr' }}>
              Scan to connect
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 6,
            }}
          >
            {contactQRs.map((qr) => (
              <div
                key={qr.label_en}
                style={{
                  border: `1.5px solid ${qr.color}`,
                  borderRadius: 6,
                  padding: 4,
                  background: '#fff',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <div
                  style={{
                    background: '#fff',
                    padding: 2,
                    borderRadius: 3,
                    lineHeight: 0,
                  }}
                >
                  <QRCodeSVG
                    value={qr.value}
                    size={54}
                    bgColor="#ffffff"
                    fgColor={qr.color}
                    level="M"
                    marginSize={0}
                  />
                </div>
                <div style={{ fontSize: 8.5, fontWeight: 700, color: qr.color, lineHeight: 1.1 }}>
                  {qr.label_ar} / {qr.label_en}
                </div>
                <div style={{ fontSize: 7.5, color: '#444', direction: 'ltr', lineHeight: 1 }}>
                  {qr.sublabel}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: 'auto',
          paddingTop: 8,
          borderTop: '2px solid #d4af37',
          textAlign: 'center',
          fontSize: 9.5,
          color: '#666',
        }}
      >
        <div style={{ fontWeight: 600 }}>
          KE Gents Saloon — UAE • {bookingUrl.replace(/^https?:\/\//, '')}
        </div>
        <div style={{ marginTop: 2 }}>
          {`طُبع في / Printed: ${new Date().toLocaleDateString('en-GB')} • ${visibleSvcs.length} خدمة / services • ${visibleOffers.length} عرض / offers`}
        </div>
      </div>
    </div>
  );
});
ServicesCatalogSheet.displayName = 'ServicesCatalogSheet';

export default ServicesCatalogSheet;
